import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { isUserWorkflow, supportsSelection } from "./workflowParticipants";
import { orderedWorkflowVersions } from "./workflowVersions";

const liveVersions = (workflow) =>
  orderedWorkflowVersions(workflow).filter(
    (version) => version.status?.toLowerCase() === "live",
  );

// Mount on opening so each audience starts with a fresh selection and preview.
export default function SchoolWorkflowEnrollmentDialog({
  api,
  schools,
  onClose,
}) {
  const [management, setManagement] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [workflowId, setWorkflowId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const schoolKey = JSON.stringify([
    ...new Set(schools.map((school) => school.id)),
  ]);
  const schoolIds = useMemo(() => JSON.parse(schoolKey), [schoolKey]);
  const previewKey = JSON.stringify([workflowId, versionId, schoolIds]);
  const currentPreview = preview?.key === previewKey ? preview : null;

  useEffect(() => {
    let active = true;
    api
      .getManagement()
      .then((result) => {
        if (active) setManagement(result);
      })
      .catch((error) => {
        if (active) setLoadError(error.message);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const workflows = (management?.marketingWorkflows || []).filter(
    (workflow) =>
      workflow.isEnabled &&
      isUserWorkflow(workflow) &&
      supportsSelection(workflow, "school") &&
      liveVersions(workflow).length,
  );
  const workflow = workflows.find((item) => item.id === workflowId);
  const versions = liveVersions(workflow);
  const paused = management?.marketingAutomationSettings?.isEnabled === false;

  useEffect(() => {
    let active = true;
    if (!workflowId || !versionId || !schoolIds.length || paused)
      return undefined;
    api
      .previewAudience({ workflowId, versionId, schoolIds })
      .then((count) => {
        if (active) setPreview({ key: previewKey, count });
      })
      .catch((error) => {
        if (active) setPreview({ key: previewKey, error: error.message });
      });
    return () => {
      active = false;
    };
  }, [api, workflowId, versionId, schoolIds, previewKey, paused]);

  const canEnroll =
    !paused &&
    !!workflow &&
    !!versionId &&
    schoolIds.length > 0 &&
    schoolIds.length <= 100 &&
    currentPreview?.count > 0;
  const enroll = async () => {
    if (submitting.current || !canEnroll) return;
    submitting.current = true;
    setBusy(true);
    try {
      const { result } = await api.enrollAudience({
        workflowId,
        versionId,
        schoolIds,
      });
      enqueueSnackbar(
        `Enrolled ${result.enrolled}; previously enrolled ${result.already_enrolled}; ineligible ${result.ineligible}; missing ${result.missing}; failed ${result.failed}.`,
        { variant: result.failed ? "warning" : "success" },
      );
      if (!result.failed) onClose();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={() => {
        if (!submitting.current) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Enroll to workflow</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>{schoolIds.length} schools selected</Typography>
          <Typography
            sx={{ maxHeight: 160, overflow: "auto", overflowWrap: "anywhere" }}
          >
            {schools.map((school) => school.name).join(", ")}
          </Typography>
          <Typography variant="body2">
            Only eligible staff will be enrolled. Students are excluded.
            Eligibility is checked again when enrolling.
          </Typography>
          {loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : !management ? (
            <Typography>Loading workflows…</Typography>
          ) : paused ? (
            <Alert severity="warning">Marketing automation is paused.</Alert>
          ) : !workflows.length ? (
            <Alert severity="info">
              No enabled customer workflows have a live version available for
              school enrollment.
            </Alert>
          ) : (
            <TextField
              select
              label="Workflow type"
              value={workflowId}
              disabled={busy}
              onChange={(event) => {
                const next = workflows.find(
                  (item) => item.id === event.target.value,
                );
                setWorkflowId(next.id);
                setVersionId(
                  liveVersions(next).find(
                    (version) => version.id === next.defaultVersion?.id,
                  )?.id || "",
                );
                setPreview(null);
              }}
            >
              {workflows.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.workflowTypeLabel} — {item.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {workflow && (
            <TextField
              select
              label="Version"
              value={versionId}
              disabled={busy}
              onChange={(event) => {
                setVersionId(event.target.value);
                setPreview(null);
              }}
              helperText="The default is preselected when available. You can choose another live version."
            >
              {versions.map((version) => (
                <MenuItem key={version.id} value={version.id}>
                  Version {version.versionNumber}
                  {version.id === workflow.defaultVersion?.id
                    ? " (default)"
                    : ""}
                </MenuItem>
              ))}
            </TextField>
          )}
          {versionId &&
            !paused &&
            (currentPreview?.error ? (
              <Alert severity="error">{currentPreview.error}</Alert>
            ) : (
              <Typography aria-live="polite">
                {currentPreview
                  ? `${currentPreview.count} eligible users selected`
                  : "Checking eligible users…"}
              </Typography>
            ))}
          {schoolIds.length > 100 && (
            <Alert severity="error">Select at most 100 schools.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={enroll}
          disabled={busy || !canEnroll}
        >
          {busy ? "Enrolling…" : "Enroll users"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
